export function sortByString(a: string, b: string): number {
  return a.toLowerCase().localeCompare(b.toLowerCase());
}

function sortByNumber(a: number, b: number): number {
  return a - b;
}

function sortByBooleanString(a: string, b: string): number {
  const aVal = a.toString().toLowerCase() === 'true' ? 1 : 0;
  const bVal = b.toString().toLowerCase() === 'true' ? 1 : 0;
  return aVal - bVal;
}

function sortByDate(a: string, b: string): number {
  return a.localeCompare(b);
}

function sortByDollarRange(a: string, b: string): number {
  return convertDollarToNumber(a) - convertDollarToNumber(b);
}

// Expect format like "$18.00 - $19.50"
function convertDollarToNumber(dollarStr: string): number {
  try {
    const cleanedStr: string = dollarStr.split('-')[0].trim().replace('$', '');

    return isNaN(parseFloat(cleanedStr)) ? -1 : parseFloat(cleanedStr);
  } catch {
    return -1;
  }
}

export interface ISortAnalytics {
  ActiveMargin: (a: number, b: number) => number;
  ActiveBidderTier: (a: number, b: number) => number;
  AdblockPct: (a: number, b: number) => number;
  AdComplete: (a: number, b: number) => number;
  AdCompleteSS: (a: number, b: number) => number;
  AdBreakPosition: (a: number, b: number) => number;
  AdDuration: (a: number, b: number) => number;
  AdFilled: (a: number, b: number) => number;
  AdImpression: (a: number, b: number) => number;
  AdImpressionSS: (a: number, b: number) => number;
  AdFirstQuartile: (a: number, b: number) => number;
  AdMidPoint: (a: number, b: number) => number;
  AdSourceID: (a: string, b: string) => number;
  AdThirdQuartile: (a: number, b: number) => number;
  Adomain: (a: string, b: string) => number;
  AdsourceName: (a: string, b: string) => number;
  AdunitPosition: (a: string, b: string) => number;
  APKBuild: (a: string, b: string) => number;
  AppBundleID: (a: string, b: string) => number;
  AppName: (a: string, b: string) => number;
  APSBidsSubmitted: (a: number, b: number) => number;
  APSKey: (a: string, b: string) => number;
  AuctionEnds: (a: number, b: number) => number;
  AuctionLatency: (a: number, b: number) => number;
  AuctionClearRate: (a: number, b: number) => number;
  AvgBid: (a: number, b: number) => number;
  AvgImpECPM: (a: number, b: number) => number;
  AvgImpGrossECPM: (a: number, b: number) => number;
  AvgStreamAdFilledLatency: (a: number, b: number) => number;
  AvgStreamChunklistResponseLatency: (a: number, b: number) => number;
  AvgStreamResponseLatency: (a: number, b: number) => number;
  AvgWinningBid: (a: number, b: number) => number;
  AvgStreamStartPerSession: (a: number, b: number) => number;
  AvgPodRequestPerSession: (a: number, b: number) => number;
  AvgPodFilledPerSession: (a: number, b: number) => number;
  AvgPodCompletePerSession: (a: number, b: number) => number;
  AvgAdFilledPerSession: (a: number, b: number) => number;
  AvgAdImpressionPerSession: (a: number, b: number) => number;
  AvgAdFirstQuartilePerSession: (a: number, b: number) => number;
  AvgAdMidPointPerSession: (a: number, b: number) => number;
  AvgAdThirdQuartilePerSession: (a: number, b: number) => number;
  AvgAdCompletePerSession: (a: number, b: number) => number;
  AvgStreamErrorPerSession: (a: number, b: number) => number;
  AvgWatchTimePerSession: (a: number, b: number) => number;
  AvgWatchTimePerViewer: (a: number, b: number) => number;
  BidAuctions: (a: number, b: number) => number;
  BidDealId: (a: string, b: string) => number;
  BidDensity: (a: string, b: string) => number;
  BidderGroup: (a: string, b: string) => number;
  BidderID: (a: string, b: string) => number;
  BidderLabelName: (a: string, b: string) => number;
  BidderName: (a: string, b: string) => number;
  BidderTier: (a: number, b: number) => number;
  BidderSubTier: (a: number, b: number) => number;
  BidderType: (a: string, b: string) => number;
  BidErrorCode: (a: string, b: string) => number;
  BidErrors: (a: number, b: number) => number;
  BidErrorRate: (a: number, b: number) => number;
  BidLosts: (a: number, b: number) => number;
  BidLossRatePrice: (a: number, b: number) => number;
  BidLossRateTier: (a: number, b: number) => number;
  BidNone: (a: number, b: number) => number;
  BidRequests: (a: number, b: number) => number;
  BidResponseDealID: (a: string, b: string) => number;
  BidResponseRate: (a: number, b: number) => number;
  BidResponses: (a: number, b: number) => number;
  BidSaverTrueLift: (a: number, b: number) => number;
  BidTimeouts: (a: number, b: number) => number;
  BidTimeoutsRate: (a: number, b: number) => number;
  BidWonRejectReason: (a: string, b: string) => number;
  BidWons: (a: number, b: number) => number;
  BounceRate: (a: number, b: number) => number;
  BrandSafetyRuleID: (a: number, b: number) => number;
  BuyersLatency: (a: number, b: number) => number;
  CampaignComplete: (a: number, b: number) => number;
  CampaignFilled: (a: number, b: number) => number;
  CampaignImpression: (a: number, b: number) => number;
  CampaignName: (a: string, b: string) => number;
  CampaignQuartile1: (a: number, b: number) => number;
  CampaignQuartile2: (a: number, b: number) => number;
  CampaignQuartile3: (a: number, b: number) => number;
  CampaignRequest: (a: number, b: number) => number;
  CampaignInventoryRequest: (a: number, b: number) => number;
  CampaignStart: (a: number, b: number) => number;
  Category: (a: string, b: string) => number;
  ChannelGroup: (a: string, b: string) => number;
  ChannelLabelName: (a: string, b: string) => number;
  City: (a: string, b: string) => number;
  ClearRateByPodDuration: (a: number, b: number) => number;
  Clicks: (a: number, b: number) => number;
  Complete: (a: number, b: number) => number;
  CompletionRate: (a: number, b: number) => number;
  ContentCat: (a: string, b: string) => number;
  ContentChannel: (a: string, b: string) => number;
  ContentContext: (a: string, b: string) => number;
  ContentDistID: (a: string, b: string) => number;
  ContentDistName: (a: string, b: string) => number;
  ContentEpisode: (a: number, b: number) => number;
  ContentGenre: (a: string, b: string) => number;
  ContentID: (a: string, b: string) => number;
  ContentKeywords: (a: string, b: string) => number;
  ContentLanguage: (a: string, b: string) => number;
  ContentLength: (a: number, b: number) => number;
  ContentNetwork: (a: string, b: string) => number;
  ContentProdQual: (a: string, b: string) => number;
  ContentProducerID: (a: string, b: string) => number;
  ContentProducerName: (a: string, b: string) => number;
  ContentRating: (a: string, b: string) => number;
  ContentSeason: (a: string, b: string) => number;
  ContentSeries: (a: string, b: string) => number;
  ContentTitle: (a: string, b: string) => number;
  Coppa: (a: string, b: string) => number;
  CostToPublisher: (a: number, b: number) => number;
  Country: (a: string, b: string) => number;
  CountryGroupName: (a: string, b: string) => number;
  CPM: (a: number, b: number) => number;
  CPMPerSecond: (a: number, b: number) => number;
  Custom1: (a: string, b: string) => number;
  Custom2: (a: string, b: string) => number;
  Custom3: (a: string, b: string) => number;
  Custom4: (a: string, b: string) => number;
  Custom5: (a: string, b: string) => number;
  Custom6: (a: string, b: string) => number;
  Custom7: (a: string, b: string) => number;
  Custom8: (a: string, b: string) => number;
  Custom9: (a: string, b: string) => number;
  Custom10: (a: string, b: string) => number;
  Custom11: (a: string, b: string) => number;
  Custom12: (a: string, b: string) => number;
  Custom13: (a: string, b: string) => number;
  Custom14: (a: string, b: string) => number;
  Custom15: (a: string, b: string) => number;
  Custom16: (a: string, b: string) => number;
  Custom17: (a: string, b: string) => number;
  Custom18: (a: string, b: string) => number;
  CustomTargeting: (a: string, b: string) => number;

  CreativeID: (a: number, b: number) => number;
  CreativeName: (a: string, b: string) => number;

  DataProvider: (a: string, b: string) => number;
  DataType: (a: string, b: string) => number;
  Date: (a: string, b: string) => number;
  DateLabel: (a: string, b: string) => number;
  Day: (a: string, b: string) => number;
  DealID: (a: string, b: string) => number;
  DealName: (a: string, b: string) => number;
  DedupBypass: (a: string, b: string) => number;
  DeliveredPodDuration: (a: number, b: number) => number;
  DemandPath: (a: string, b: string) => number;
  DemandSidePlatformID: (a: number, b: number) => number;
  DemandType: (a: string, b: string) => number;
  DestinationErrorCode: (a: number, b: number) => number;
  DestinationID: (a: number, b: number) => number;
  DestinationImpressions: (a: number, b: number) => number;
  DestinationName: (a: string, b: string) => number;
  DestinationSiteID: (a: number, b: number) => number;
  DestinationSiteName: (a: string, b: string) => number;
  DestinationType: (a: number, b: number) => number;
  DestinationRequests: (a: number, b: number) => number;
  DestinationResponses: (a: number, b: number) => number;
  DestinationTimeouts: (a: number, b: number) => number;
  DestinationRequestErrors: (a: number, b: number) => number;
  DestinationNoFills: (a: number, b: number) => number;
  DestinationRequestLatency: (a: number, b: number) => number;
  Device: (a: string, b: string) => number;
  DeviceGeoRegion: (a: string, b: string) => number;
  DeviceMake: (a: string, b: string) => number;
  DeviceModel: (a: string, b: string) => number;
  DeviceOS: (a: string, b: string) => number;
  DeviceType: (a: string, b: string) => number;
  DeviceSubType: (a: string, b: string) => number;
  DMA: (a: string, b: string) => number;
  DoNotTrack: (a: string, b: string) => number;
  DspName: (a: string, b: string) => number;
  Duration: (a: number, b: number) => number;
  DurationExceeded: (a: number, b: number) => number;
  EndpointErrorCode: (a: string, b: string) => number;
  EndpointLatency: (a: number, b: number) => number;
  EndpointRequests: (a: number, b: number) => number;
  EndpointRequestECPM: (a: number, b: number) => number;
  EventEndpointErrors: (a: number, b: number) => number;
  FlightDateCheckpointID: (a: string, b: string) => number;
  FlightDateCheckpointName: (a: string, b: string) => number;
  FilledPodDuration: (a: number, b: number) => number;
  FilledVMAPDuration: (a: number, b: number) => number;
  FillRate: (a: number, b: number) => number;
  FillRateByDemandSource: (a: number, b: number) => number;
  FilledAds: (a: number, b: number) => number;
  FwSectionName: (a: string, b: string) => number;
  FwSectionIdV2: (a: string, b: string) => number;
  FwRenderRate: (a: number, b: number) => number;
  FwWinRate: (a: number, b: number) => number;
  DeliveredAds: (a: number, b: number) => number;
  Gdpr: (a: string, b: string) => number;
  GdprConsent: (a: string, b: string) => number;
  GlobalMediafileID: (a: number, b: number) => number;
  GlobalVastID: (a: number, b: number) => number;
  Grouplabel: (a: string, b: string) => number;
  GrossRevenue: (a: number, b: number) => number;
  Hour: (a: string, b: string) => number;
  HLSReject: (a: number, b: number) => number;
  ImpressedBidderType: (a: string, b: string) => number;
  Impressions: (a: number, b: number) => number;
  InboundDealId: (a: string, b: string) => number;
  InboundWseat: (a: string, b: string) => number;
  InventoryRouteID: (a: number, b: number) => number;
  InventoryRouteName: (a: string, b: string) => number;
  InventoryRouteCondition: (a: number, b: number) => number;
  InventoryRouteErrorCode: (a: number, b: number) => number;
  InventoryRouteRequests: (a: number, b: number) => number;
  InventoryRouteResponses: (a: number, b: number) => number;
  InventoryRouteErrors: (a: number, b: number) => number;
  InventoryRouteNoFills: (a: number, b: number) => number;
  InventoryRouteEndpointLatency: (a: number, b: number) => number;
  InventoryRouteResponseDestinationID: (a: number, b: number) => number;
  InventoryType: (a: number, b: number) => number;
  IrisContext: (a: string, b: string) => number;
  IrisID: (a: string, b: string) => number;
  IsBidSaver: (a: string, b: string) => number;
  IsFallbackResponse: (a: string, b: string) => number;
  IsMultiBid: (a: string, b: string) => number;
  IsPartialAdBreak: (a: string, b: string) => number;
  IsPoddedRequest: (a: string, b: string) => number;
  Language: (a: string, b: string) => number;
  LineItemAlias: (a: string, b: string) => number;
  LineItemCount: (a: number, b: number) => number;
  LineItemStatus: (a: string, b: string) => number;
  LineItemID: (a: string, b: string) => number;
  LiverampCPM: (a: number, b: number) => number;
  LiverampRevenue: (a: number, b: number) => number;
  LiverampSegmentIDs: (a: string, b: string) => number;
  Livestream: (a: string, b: string) => number;
  MaxAvailablePodDurationByTier: (a: number, b: number) => number;
  MediaFile: (a: string, b: string) => number;
  Month: (a: string, b: string) => number;
  NativeCompanionClick: (a: number, b: number) => number;
  NativeCreativeView: (a: number, b: number) => number;
  NativeVideoClick: (a: number, b: number) => number;
  NeedsCreative: (a: string, b: string) => number;
  NetUnitCost: (a: number, b: number) => number;
  NetworkID: (a: number, b: number) => number;
  NetworkName: (a: string, b: string) => number;
  NetworkChannelID: (a: number, b: number) => number;
  NetworkChannelName: (a: string, b: string) => number;
  NetworkChannelSiteID: (a: number, b: number) => number;
  NetworkChannelSiteName: (a: string, b: string) => number;
  NumberOfAdsRequested: (a: number, b: number) => number;
  NumOfUsers: (a: number, b: number) => number;
  OrderAlias: (a: string, b: string) => number;
  ORTBSiteName: (a: string, b: string) => number;
  ORTBWins: (a: number, b: number) => number;
  OperatingSystem: (a: string, b: string) => number;
  OutboundDealId: (a: string, b: string) => number;
  PageLoadTime: (a: number, b: number) => number;
  PageViews: (a: number, b: number) => number;
  PaidFilledPodDuration: (a: number, b: number) => number;
  PaidPodClearRate: (a: number, b: number) => number;
  PartnerCPM: (a: number, b: number) => number;
  PartnerRevenue: (a: number, b: number) => number;
  PhiloFallback: (a: string, b: string) => number;
  PodClearRate: (a: number, b: number) => number;
  PodStart: (a: number, b: number) => number;
  PodStartSS: (a: number, b: number) => number;
  PodComplete: (a: number, b: number) => number;
  PodCompleteSS: (a: number, b: number) => number;
  PodCompleteRate: (a: number, b: number) => number;
  PodDuration: (a: number, b: number) => number;
  PodFilled: (a: number, b: number) => number;
  PodFillRate: (a: number, b: number) => number;
  PodRequest: (a: number, b: number) => number;
  PresetAggregateForLastSevenDays: (a: number, b: number) => number;
  PresetAggregateForLastThirtyDays: (a: number, b: number) => number;
  PresetAggregateForYesterday: (a: number, b: number) => number;
  PublicaCPM: (a: number, b: number) => number;
  PublicaRevenue: (a: number, b: number) => number;
  PublisherCPM: (a: number, b: number) => number;
  PublisherMargin: (a: number, b: number) => number;
  PublisherName: (a: string, b: string) => number;
  Quartile1: (a: number, b: number) => number;
  Quartile2: (a: number, b: number) => number;
  Quartile3: (a: number, b: number) => number;
  RecognitionResultEnrichedLanguageInput: (a: string, b: string) => number;
  RecoveryActive: (a: number, b: number) => number;
  RenderRate: (a: number, b: number) => number;
  RenderRateByPodDuration: (a: number, b: number) => number;
  RequestedPodDuration: (a: number, b: number) => number;
  RequestedVMAPDuration: (a: number, b: number) => number;
  Requests: (a: number, b: number) => number;
  Revenue: (a: number, b: number) => number;
  RokuAdKeys: (a: string, b: string) => number;
  SamsungModel: (a: string, b: string) => number;
  SegmentIDs: (a: string, b: string) => number;
  SegmentName: (a: string, b: string) => number;
  SessionsPerUser: (a: number, b: number) => number;
  SetTargetings: (a: number, b: number) => number;
  SlotNumber: (a: number, b: number) => number;
  SharedFilledVMAPDuration: (a: number, b: number) => number;
  SharedRequestedVMAPDuration: (a: number, b: number) => number;
  ShareOfVoice: (a: number, b: number) => number;
  ShareOfVoiceType: (a: number, b: number) => number;
  ShareType: (a: number, b: number) => number;
  SiteID: (a: number, b: number) => number;
  SiteName: (a: string, b: string) => number;
  SitePage: (a: string, b: string) => number;
  Skip: (a: number, b: number) => number;
  SkipOffset: (a: number, b: number) => number;
  SOVInventoryType: (a: string, b: string) => number;
  SOVParallelAuctionsEnabled: (a: number, b: number) => number;
  SOVParallelAuctionsBackfillEnabled: (a: number, b: number) => number;
  SpotxEndpoint: (a: number, b: number) => number;
  SpotxParamChannelID: (a: number, b: number) => number;
  StartDate: (a: string, b: string) => number;
  StreamAdsFetchTimeout: (a: number, b: number) => number;
  StreamChunklistTimeout: (a: number, b: number) => number;
  StreamError: (a: number, b: number) => number;
  StreamErrorCode: (a: string, b: string) => number;
  StreamFormatType: (a: string, b: string) => number;
  StreamPlaylistTimeout: (a: number, b: number) => number;
  StreamStart: (a: number, b: number) => number;
  TotalRequestedPodDuration: (a: number, b: number) => number;
  TotalFilledPodDuration: (a: number, b: number) => number;
  TotalPaidFilledPodDuration: (a: number, b: number) => number;
  TotalDeliveredPodDuration: (a: number, b: number) => number;
  SyntheticID: (a: number, b: number) => number;
  TargetingDevice: (a: string, b: string) => number;
  TopCountries: (a: string, b: string) => number;
  TrafficType: (a: string, b: string) => number;
  UniqueSessions: (a: number, b: number) => number;
  UniqueUsers: (a: number, b: number) => number;
  UniqueViewers: (a: number, b: number) => number;
  UsPrivacy: (a: string, b: string) => number;
  VastErrorCode: (a: string, b: string) => number;
  VastErrors: (a: number, b: number) => number;
  VenueID: (a: string, b: string) => number;
  ViewsPerSession: (a: number, b: number) => number;
  VMAPLatency: (a: number, b: number) => number;
  VMAPRequests: (a: number, b: number) => number;
  VMAPResponses: (a: number, b: number) => number;
  VPAIDErrors: (a: number, b: number) => number;
  VPAIDStart: (a: number, b: number) => number;
  VPAIDImpressions: (a: number, b: number) => number;
  VPAIDQuartile1: (a: number, b: number) => number;
  VPAIDQuartile2: (a: number, b: number) => number;
  VPAIDQuartile3: (a: number, b: number) => number;
  VPAIDComplete: (a: number, b: number) => number;
  WeekOfYear: (a: number, b: number) => number;
  WinRate: (a: number, b: number) => number;
  Zip: (a: string, b: string) => number;

  // IAS First and Third Party Group Bys
  MediaTypeID: (a: string, b: string) => number;
  // IAS First and Third Party Metrics
  TrackedAds: (a: number, b: number) => number;
  ImpressionRendered: (a: number, b: number) => number;
  IVTAds: (a: number, b: number) => number;
  IVTRate: (a: number, b: number) => number;
  MeasuredAds: (a: number, b: number) => number;
  MeasuredRate: (a: number, b: number) => number;
  ViewableImpression: (a: number, b: number) => number;
  ViewabilityRate: (a: number, b: number) => number;
  // IAS First Party Metrics (render rate already exist)
  EligibleAds: (a: number, b: number) => number;
  AdBlockCount: (a: number, b: number) => number;
  AdBlockRate: (a: number, b: number) => number;
  // IAS First Party Group Bys (LineItemID, CreativeID, CreativeName, AdvertiserName, OrderName already exist)
  AdvertiserID: (a: string, b: string) => number;
  AdUnitID: (a: string, b: string) => number;
  AdSize: (a: string, b: string) => number;
  OrderID: (a: string, b: string) => number;
  AdUnitPath: (a: string, b: string) => number;
  LineItemName: (a: string, b: string) => number;
  // IAS Third Party Group Bys
  PublicaChannelID: (a: string, b: string) => number;
  PublicaBidderID: (a: string, b: string) => number;
  PublisherID: (a: string, b: string) => number;
  PlacementID: (a: string, b: string) => number;
  // IAS Third Party Metrics
  GIVTAds: (a: number, b: number) => number;
  GIVTRate: (a: number, b: number) => number;
  SIVTAds: (a: number, b: number) => number;
  SIVTRate: (a: number, b: number) => number;
  URLFailRate: (a: number, b: number) => number;
  IVTFailRate: (a: number, b: number) => number;
  ValidVideoAds: (a: number, b: number) => number;
  Quartile1Rate: (a: number, b: number) => number;
  Quartile2Rate: (a: number, b: number) => number;
  Quartile3Rate: (a: number, b: number) => number;
  OnscreenComplete: (a: number, b: number) => number;
  OnscreenCompletionRate: (a: number, b: number) => number;
  PassedImps: (a: number, b: number) => number;
  PassRate: (a: number, b: number) => number;
  FailedImps: (a: number, b: number) => number;
  FailedAdRate: (a: number, b: number) => number;
  ValidViewableVideoAds: (a: number, b: number) => number;
  ViewableQuartile1: (a: number, b: number) => number;
  ViewableQuartile2: (a: number, b: number) => number;
  ViewableQuartile3: (a: number, b: number) => number;
  ViewableComplete: (a: number, b: number) => number;
  ViewableQuartile1Rate: (a: number, b: number) => number;
  ViewableQuartile2Rate: (a: number, b: number) => number;
  ViewableQuartile3Rate: (a: number, b: number) => number;
  ViewableCompleteRate: (a: number, b: number) => number;
  UnmeasuredImps: (a: number, b: number) => number;
  UnmeasuredImpsRate: (a: number, b: number) => number;
  NonViewableImpression: (a: number, b: number) => number;
  NonViewableImpRate: (a: number, b: number) => number;
  // IAS Performance Report Metrics
  MrcOutOfViewNEPImps: (a: number, b: number) => number;
  MrcOutOfViewNIVImps: (a: number, b: number) => number;
  MrcOutOfViewOOFImps: (a: number, b: number) => number;
  MrcOutOfViewNSImps: (a: number, b: number) => number;
  MrcOutOfViewUNKImps: (a: number, b: number) => number;
  BlockedContentImps: (a: number, b: number) => number;
  BlockedCustomListImps: (a: number, b: number) => number;
  BlockedGeoImps: (a: number, b: number) => number;
  BlockedKeywordImps: (a: number, b: number) => number;
  BlockedSuitabilityImps: (a: number, b: number) => number;
  BlockedSeeThroughImps: (a: number, b: number) => number;
  BlockedSuspiciousImps: (a: number, b: number) => number;
  BlockedLanguageImps: (a: number, b: number) => number;
  FraudGivtImps: (a: number, b: number) => number;
  FraudNhtImps: (a: number, b: number) => number;
  FraudHaImps: (a: number, b: number) => number;
  FraudDsImps: (a: number, b: number) => number;
  FraudIbImps: (a: number, b: number) => number;
  FraudLspImps: (a: number, b: number) => number;
  FraudSivtImps: (a: number, b: number) => number;
  RefreshImps: (a: number, b: number) => number;
  GroupmFullyInViewVideoImps: (a: number, b: number) => number;
  GroupmFullyInViewDisplayImps: (a: number, b: number) => number;
  TotalBillableImps: (a: number, b: number) => number;
  SittingDuckBotImps: (a: number, b: number) => number;
  StandardBotImps: (a: number, b: number) => number;
  VolunteerBotImps: (a: number, b: number) => number;
  ProfileBotImps: (a: number, b: number) => number;
  MaskedBotImps: (a: number, b: number) => number;
  NomadicBotImps: (a: number, b: number) => number;
  OtherBotImps: (a: number, b: number) => number;
  FailedAdultAds: (a: number, b: number) => number;
  FailedAlcoholAds: (a: number, b: number) => number;
  FailedHateSpeechAds: (a: number, b: number) => number;
  FailedIllegalDownloadsAds: (a: number, b: number) => number;
  FailedDrugsAds: (a: number, b: number) => number;
  FailedOffensiveLanguageAds: (a: number, b: number) => number;
  RefreshThresholdEvents: (a: number, b: number) => number;
  PublicisFullyInViewDisplayImps: (a: number, b: number) => number;
  PublicisFullyInViewVideoImps: (a: number, b: number) => number;
  MrcOvrNetImps: (a: number, b: number) => number;
  MrcOvrNepImps: (a: number, b: number) => number;
  MrcOvrNivImps: (a: number, b: number) => number;
  MrcOvrOofImps: (a: number, b: number) => number;
  MrcOvrNsImps: (a: number, b: number) => number;
  MrcOvrUnkImps: (a: number, b: number) => number;
  FailedContentImps: (a: number, b: number) => number;
  FailedSuitabilityImps: (a: number, b: number) => number;
  FailedKeywordImps: (a: number, b: number) => number;
  FailedCustomListImps: (a: number, b: number) => number;
  FailedGeoImps: (a: number, b: number) => number;
  FailedSuspiciousImps: (a: number, b: number) => number;
  FailedSeeThroughImps: (a: number, b: number) => number;
  FailedLanguageImps: (a: number, b: number) => number;
  BlockedContentAdultImps: (a: number, b: number) => number;
  BlockedContentAlcoholImps: (a: number, b: number) => number;
  BlockedContentHateImps: (a: number, b: number) => number;
  BlockedContentDownloadsImps: (a: number, b: number) => number;
  BlockedContentDrugsImps: (a: number, b: number) => number;
  BlockedContentOffensiveImps: (a: number, b: number) => number;
  BlockedContentViolenceImps: (a: number, b: number) => number;
  SophisticatedInvalidTrafficAds: (a: number, b: number) => number;
  ReducedValueInventoryAds: (a: number, b: number) => number;
  FraudGIVTRate: (a: number, b: number) => number;
  FraudNHTRate: (a: number, b: number) => number;
  FraudHARate: (a: number, b: number) => number;
  FraudDSRate: (a: number, b: number) => number;
  BlockedContentRate: (a: number, b: number) => number;
  BlockedCustomListRate: (a: number, b: number) => number;
  BlockedGeoRate: (a: number, b: number) => number;
  BlockedKeywordRate: (a: number, b: number) => number;
  BlockedSuitabilityRate: (a: number, b: number) => number;
  BlockedSeeThroughRate: (a: number, b: number) => number;
  BlockedSuspiciousRate: (a: number, b: number) => number;
  BlockedLanguageRate: (a: number, b: number) => number;
  RefreshedAdRate: (a: number, b: number) => number;
  IASRefreshedAdRate: (a: number, b: number) => number;
  PublicisDisplayViewableRate: (a: number, b: number) => number;
  PublicisVideoViewableRate: (a: number, b: number) => number;
  InViewAtPageOpenRate: (a: number, b: number) => number;
  InViewOver5sRate: (a: number, b: number) => number;
  InViewOver15sRate: (a: number, b: number) => number;
  VideoInViewRate: (a: number, b: number) => number;
  VideoOutOfViewRate: (a: number, b: number) => number;
  VideoNeverStartedRate: (a: number, b: number) => number;
  VideoCompletedRate: (a: number, b: number) => number;
  VideoCompletedInViewRate: (a: number, b: number) => number;
  VideoMutedRate: (a: number, b: number) => number;
  VideoFullScreenRate: (a: number, b: number) => number;
  VideoClickThroughRate: (a: number, b: number) => number;
  GeneralInvalidTrafficRate: (a: number, b: number) => number;
  SophisticatedInvalidTrafficRate: (a: number, b: number) => number;
  ReducedValueInventoryRate: (a: number, b: number) => number;
  ReviewStatus: (a: number, b: number) => number;
  NumberOfBlocks: (a: string, b: string) => number;
  Username: (a: string, b: string) => number;
  Timestamp: (a: string, b: string) => number;
  PrebidErrors: (a: number, b: number) => number;
  PostbidErrors: (a: number, b: number) => number;
  BidRejections: (a: number, b: number) => number;

  // Elea AI
  EleaAIBidErrors: (a: number, b: number) => number;
  EleaAIBidErrorsDifferentAdomain: (a: number, b: number) => number;
  EleaAIBidErrorsDifferentAdomainRate: (a: number, b: number) => number;
  EleaAIBidLosts: (a: number, b: number) => number;
  EleaAIBidLostsDifferentAdomain: (a: number, b: number) => number;
  EleaAIBidLostsDifferentAdomainRate: (a: number, b: number) => number;
  EleaAIBidResponses: (a: number, b: number) => number;
  EleaAIBidResponsesDifferentAdomain: (a: number, b: number) => number;
  EleaAIBidResponsesDifferentAdomainRate: (a: number, b: number) => number;
  EleaAIBidWons: (a: number, b: number) => number;
  EleaAIBidWonsDifferentAdomain: (a: number, b: number) => number;
  EleaAIBidWonsDifferentAdomainRate: (a: number, b: number) => number;
  EleaAIDetectedLanguage: (a: string, b: string) => number;
  EleaAIEnrichedAdomain: (a: string, b: string) => number;
  EleaAIImpressions: (a: number, b: number) => number;
  EleaAIImpressionsDifferentAdomain: (a: number, b: number) => number;
  EleaAIImpressionsDifferentAdomainRate: (a: number, b: number) => number;
  EleaAIMediafile: (a: string, b: string) => number;
  EleaAIOriginalAdomain: (a: string, b: string) => number;
  EleaAIDetectedLanguageBlockedBids: (a: number, b: number) => number;
  EleaAIBidWonRate: (a: number, b: number) => number;
  EleaAIImpressionRate: (a: number, b: number) => number;
  EleaAINumTotalAdomainsBidErrors: (a: number, b: number) => number;
  EleaAINumTotalAdomainsBidLosts: (a: number, b: number) => number;
  EleaAINumTotalAdomainsBidResponses: (a: number, b: number) => number;
  EleaAINumTotalAdomainsBidWons: (a: number, b: number) => number;
  EleaAINumTotalAdomainsImpressions: (a: number, b: number) => number;
  EleaAINumTotalDetectedLanguagesBidErrors: (a: number, b: number) => number;
  EleaAINumTotalDetectedLanguagesBidLosts: (a: number, b: number) => number;
  EleaAINumTotalDetectedLanguagesBidResponses: (a: number, b: number) => number;
  EleaAINumTotalDetectedLanguagesBidWons: (a: number, b: number) => number;
  EleaAINumTotalDetectedLanguagesImpressions: (a: number, b: number) => number;
  EleaAINumTotalMediafilesBidErrors: (a: number, b: number) => number;
  EleaAINumTotalMediafilesBidLosts: (a: number, b: number) => number;
  EleaAINumTotalMediafilesBidResponses: (a: number, b: number) => number;
  EleaAINumTotalMediafilesBidWons: (a: number, b: number) => number;
  EleaAINumTotalMediafilesImpressions: (a: number, b: number) => number;
  EleaAINumUniqueAdomainsBidErrors: (a: number, b: number) => number;
  EleaAINumUniqueAdomainsBidLosts: (a: number, b: number) => number;
  EleaAINumUniqueAdomainsBidResponses: (a: number, b: number) => number;
  EleaAINumUniqueAdomainsBidWons: (a: number, b: number) => number;
  EleaAINumUniqueAdomainsImpressions: (a: number, b: number) => number;
  EleaAINumUniqueDetectedLanguagesBidErrors: (a: number, b: number) => number;
  EleaAINumUniqueDetectedLanguagesBidLosts: (a: number, b: number) => number;
  EleaAINumUniqueDetectedLanguagesBidResponses: (a: number, b: number) => number;
  EleaAINumUniqueDetectedLanguagesBidWons: (a: number, b: number) => number;
  EleaAINumUniqueDetectedLanguagesImpressions: (a: number, b: number) => number;
  EleaAINumUniqueMediafilesBidErrors: (a: number, b: number) => number;
  EleaAINumUniqueMediafilesBidLosts: (a: number, b: number) => number;
  EleaAINumUniqueMediafilesBidResponses: (a: number, b: number) => number;
  EleaAINumUniqueMediafilesBidWons: (a: number, b: number) => number;
  EleaAINumUniqueMediafilesImpressions: (a: number, b: number) => number;
  // Pod Hourly Report
  AdvertiserRuleCpmRev: (a: number, b: number) => number;
  AdvertiserRuleCpmsRev: (a: number, b: number) => number;
  AdvertiserRulePodLift: (a: number, b: number) => number;
  CpmRev: (a: number, b: number) => number;
  CpmsRev: (a: number, b: number) => number;
  EventPodCompare: (a: number, b: number) => number;
  EventPodCompareWithAdvertiserRules: (a: number, b: number) => number;
  EventPodCompareWithoutAdvertiserRules: (a: number, b: number) => number;
  IsPhiloFallbackAuction: (a: string, b: string) => number;
  NoBidSaverRev: (a: number, b: number) => number;
  NumberOfWinners: (a: number, b: number) => number;
  PodComparePodDuration: (a: number, b: number) => number;
  PodCompareRevenue: (a: number, b: number) => number;
  PodCompareType: (a: number, b: number) => number;
  PodDurationBackfilled: (a: number, b: number) => number;
  PodDurationOwnedByNonSOVBidder: (a: number, b: number) => number;
  PodDurationOwnedBySOVBidder: (a: number, b: number) => number;
  PodLevelSOVType: (a: number, b: number) => number;
  TTDResponses: (a: number, b: number) => number;
  TTDSpend: (a: number, b: number) => number;
  TTDWins: (a: number, b: number) => number;
  TTDLosses: (a: number, b: number) => number;
  TTDLossesDoNotAir: (a: number, b: number) => number;
  TTDLossesCompSepSelf: (a: number, b: number) => number;
  TTDLossesCompSepOther: (a: number, b: number) => number;
  TTDLossesPriceOrTier: (a: number, b: number) => number;
  TTDLossesOther: (a: number, b: number) => number;
  HasAdvertiserRuleResponses: (a: string, b: string) => number;
  // Third Party Vast Tracker Domains Report
  VastTrackerEndpointDomain: (a: string, b: string) => number;
  TTDLossesPrice: (a: number, b: number) => number;
  TTDLossesTier: (a: number, b: number) => number;
  // Bid Floor Settings Report Only
  BidFloor: (a: number, b: number) => number;
}

export const sortAnalyticsFunctions: ISortAnalytics = {
  ActiveBidderTier: sortByNumber,
  ActiveMargin: sortByNumber,
  AdblockPct: sortByNumber,
  AdComplete: sortByNumber,
  AdCompleteSS: sortByNumber,
  AdBreakPosition: sortByNumber,
  AdDuration: sortByNumber,
  AdFilled: sortByNumber,
  AdImpression: sortByNumber,
  AdImpressionSS: sortByNumber,
  AdFirstQuartile: sortByNumber,
  AdMidPoint: sortByNumber,
  AdThirdQuartile: sortByNumber,
  Adomain: sortByString,
  AdsourceName: sortByString,
  AdSourceID: sortByString,
  AdunitPosition: sortByString,
  APKBuild: sortByString,
  AppBundleID: sortByString,
  AppName: sortByString,
  APSBidsSubmitted: sortByNumber,
  APSKey: sortByString,
  AuctionEnds: sortByNumber,
  AuctionLatency: sortByNumber,
  AuctionClearRate: sortByNumber,
  AvgBid: sortByNumber,
  AvgImpECPM: sortByNumber,
  AvgImpGrossECPM: sortByNumber,
  AvgStreamAdFilledLatency: sortByNumber,
  AvgStreamChunklistResponseLatency: sortByNumber,
  AvgStreamResponseLatency: sortByNumber,
  AvgWinningBid: sortByNumber,
  AvgStreamStartPerSession: sortByNumber,
  AvgPodRequestPerSession: sortByNumber,
  AvgPodFilledPerSession: sortByNumber,
  AvgPodCompletePerSession: sortByNumber,
  AvgAdFilledPerSession: sortByNumber,
  AvgAdImpressionPerSession: sortByNumber,
  AvgAdFirstQuartilePerSession: sortByNumber,
  AvgAdMidPointPerSession: sortByNumber,
  AvgAdThirdQuartilePerSession: sortByNumber,
  AvgAdCompletePerSession: sortByNumber,
  AvgStreamErrorPerSession: sortByNumber,
  AvgWatchTimePerSession: sortByNumber,
  AvgWatchTimePerViewer: sortByNumber,
  BidAuctions: sortByNumber,
  BidDealId: sortByString,
  BidDensity: sortByDollarRange,
  BidderGroup: sortByString,
  BidderID: sortByString,
  BidderLabelName: sortByString,
  BidderName: sortByString,
  BidderTier: sortByNumber,
  BidderSubTier: sortByNumber,
  BidderType: sortByString,
  BidErrorCode: sortByString,
  BidErrors: sortByNumber,
  BidErrorRate: sortByNumber,
  BidLosts: sortByNumber,
  BidLossRatePrice: sortByNumber,
  BidLossRateTier: sortByNumber,
  BidNone: sortByNumber,
  BidRequests: sortByNumber,
  BidResponseDealID: sortByString,
  BidResponseRate: sortByNumber,
  BidResponses: sortByNumber,
  BidSaverTrueLift: sortByNumber,
  BidTimeouts: sortByNumber,
  BidTimeoutsRate: sortByNumber,
  BidWonRejectReason: sortByString,
  BidWons: sortByNumber,
  BounceRate: sortByNumber,
  BrandSafetyRuleID: sortByNumber,
  BuyersLatency: sortByNumber,
  CampaignComplete: sortByNumber,
  CampaignFilled: sortByNumber,
  CampaignImpression: sortByNumber,
  CampaignName: sortByString,
  CampaignQuartile1: sortByNumber,
  CampaignQuartile2: sortByNumber,
  CampaignQuartile3: sortByNumber,
  CampaignRequest: sortByNumber,
  CampaignInventoryRequest: sortByNumber,
  CampaignStart: sortByNumber,
  Category: sortByString,
  ChannelGroup: sortByString,
  ChannelLabelName: sortByString,
  City: sortByString,
  ClearRateByPodDuration: sortByNumber,
  Clicks: sortByNumber,
  Complete: sortByNumber,
  CompletionRate: sortByNumber,
  ContentCat: sortByString,
  ContentChannel: sortByString,
  ContentContext: sortByString,
  ContentDistID: sortByString,
  ContentDistName: sortByString,
  ContentEpisode: sortByNumber,
  ContentGenre: sortByString,
  ContentID: sortByString,
  ContentKeywords: sortByString,
  ContentLanguage: sortByString,
  ContentLength: sortByNumber,
  ContentNetwork: sortByString,
  ContentProdQual: sortByString,
  ContentProducerID: sortByString,
  ContentProducerName: sortByString,
  ContentRating: sortByString,
  ContentSeason: sortByString,
  ContentSeries: sortByString,
  ContentTitle: sortByString,
  Coppa: sortByString,
  CostToPublisher: sortByNumber,
  Country: sortByString,
  CountryGroupName: sortByString,
  CPM: sortByNumber,
  CPMPerSecond: sortByNumber,
  CreativeID: sortByNumber,
  CreativeName: sortByString,
  Custom1: sortByString,
  Custom2: sortByString,
  Custom3: sortByString,
  Custom4: sortByString,
  Custom5: sortByString,
  Custom6: sortByString,
  Custom7: sortByString,
  Custom8: sortByString,
  Custom9: sortByString,
  Custom10: sortByString,
  Custom11: sortByString,
  Custom12: sortByString,
  Custom13: sortByString,
  Custom14: sortByString,
  Custom15: sortByString,
  Custom16: sortByString,
  Custom17: sortByString,
  Custom18: sortByString,
  CustomTargeting: sortByString,
  DataProvider: sortByString,
  DataType: sortByString,
  Date: sortByString,
  DateLabel: sortByString,
  Day: sortByDate,
  DealID: sortByString,
  DealName: sortByString,
  DedupBypass: sortByBooleanString,
  DeliveredPodDuration: sortByNumber,
  DemandPath: sortByString,
  DemandSidePlatformID: sortByNumber,
  DemandType: sortByString,
  DestinationErrorCode: sortByNumber,
  DestinationID: sortByNumber,
  DestinationImpressions: sortByNumber,
  DestinationName: sortByString,
  DestinationSiteID: sortByNumber,
  DestinationSiteName: sortByString,
  DestinationType: sortByNumber,
  DestinationRequests: sortByNumber,
  DestinationResponses: sortByNumber,
  DestinationTimeouts: sortByNumber,
  DestinationRequestErrors: sortByNumber,
  DestinationNoFills: sortByNumber,
  DestinationRequestLatency: sortByNumber,
  Device: sortByString,
  DeviceGeoRegion: sortByString,
  DeviceMake: sortByString,
  DeviceModel: sortByString,
  DeviceOS: sortByString,
  DeviceType: sortByString,
  DeviceSubType: sortByString,
  DMA: sortByString,
  DoNotTrack: sortByString,
  DspName: sortByString,
  Duration: sortByNumber,
  DurationExceeded: sortByNumber,
  EndpointErrorCode: sortByString,
  EndpointLatency: sortByNumber,
  EndpointRequests: sortByNumber,
  EndpointRequestECPM: sortByNumber,
  EventEndpointErrors: sortByNumber,
  FlightDateCheckpointID: sortByString,
  FlightDateCheckpointName: sortByString,
  FilledPodDuration: sortByNumber,
  FilledVMAPDuration: sortByNumber,
  FillRate: sortByNumber,
  FillRateByDemandSource: sortByNumber,
  FilledAds: sortByNumber,
  FwRenderRate: sortByNumber,
  FwWinRate: sortByNumber,
  FwSectionName: sortByString,
  FwSectionIdV2: sortByString,
  DeliveredAds: sortByNumber,
  Gdpr: sortByString,
  GdprConsent: sortByString,
  GlobalMediafileID: sortByNumber,
  GlobalVastID: sortByNumber,
  Grouplabel: sortByString,
  GrossRevenue: sortByNumber,
  HLSReject: sortByNumber,
  Hour: sortByString,
  ImpressedBidderType: sortByString,
  Impressions: sortByNumber,
  InboundDealId: sortByString,
  InboundWseat: sortByString,
  InventoryRouteID: sortByNumber,
  InventoryRouteName: sortByString,
  InventoryRouteCondition: sortByNumber,
  InventoryRouteErrorCode: sortByNumber,
  InventoryRouteRequests: sortByNumber,
  InventoryRouteResponses: sortByNumber,
  InventoryRouteErrors: sortByNumber,
  InventoryRouteNoFills: sortByNumber,
  InventoryRouteEndpointLatency: sortByNumber,
  InventoryRouteResponseDestinationID: sortByNumber,
  InventoryType: sortByNumber,
  IrisContext: sortByString,
  IrisID: sortByString,
  IsBidSaver: sortByBooleanString,
  IsFallbackResponse: sortByBooleanString,
  IsMultiBid: sortByBooleanString,
  IsPartialAdBreak: sortByBooleanString,
  IsPoddedRequest: sortByBooleanString,
  Language: sortByString,
  LineItemAlias: sortByString,
  LineItemCount: sortByNumber,
  LineItemID: sortByString,
  LineItemStatus: sortByString,
  LiverampCPM: sortByNumber,
  LiverampRevenue: sortByNumber,
  LiverampSegmentIDs: sortByString,
  Livestream: sortByString,
  MaxAvailablePodDurationByTier: sortByNumber,
  MediaFile: sortByString,
  Month: sortByDate,
  NativeCompanionClick: sortByNumber,
  NativeCreativeView: sortByNumber,
  NativeVideoClick: sortByNumber,
  NeedsCreative: sortByString,
  NetUnitCost: sortByNumber,
  NetworkID: sortByNumber,
  NetworkName: sortByString,
  NetworkChannelID: sortByNumber,
  NetworkChannelName: sortByString,
  NetworkChannelSiteID: sortByNumber,
  NetworkChannelSiteName: sortByString,
  NumberOfAdsRequested: sortByNumber,
  NumOfUsers: sortByNumber,
  OperatingSystem: sortByString,
  OrderAlias: sortByString,
  ORTBSiteName: sortByString,
  ORTBWins: sortByNumber,
  OutboundDealId: sortByString,
  PageLoadTime: sortByNumber,
  PageViews: sortByNumber,
  PaidFilledPodDuration: sortByNumber,
  PaidPodClearRate: sortByNumber,
  PartnerCPM: sortByNumber,
  PartnerRevenue: sortByNumber,
  PhiloFallback: sortByString,
  PodClearRate: sortByNumber,
  PodStart: sortByNumber,
  PodStartSS: sortByNumber,
  PodComplete: sortByNumber,
  PodCompleteSS: sortByNumber,
  PodCompleteRate: sortByNumber,
  PodDuration: sortByNumber,
  PodFilled: sortByNumber,
  PodFillRate: sortByNumber,
  PodRequest: sortByNumber,
  PresetAggregateForLastSevenDays: sortByNumber,
  PresetAggregateForLastThirtyDays: sortByNumber,
  PresetAggregateForYesterday: sortByNumber,
  PublicaCPM: sortByNumber,
  PublicaRevenue: sortByNumber,
  PublisherCPM: sortByNumber,
  PublisherMargin: sortByNumber,
  PublisherName: sortByString,
  Quartile1: sortByNumber,
  Quartile2: sortByNumber,
  Quartile3: sortByNumber,
  RecognitionResultEnrichedLanguageInput: sortByString,
  RecoveryActive: sortByNumber,
  RenderRate: sortByNumber,
  RenderRateByPodDuration: sortByNumber,
  RequestedPodDuration: sortByNumber,
  RequestedVMAPDuration: sortByNumber,
  Requests: sortByNumber,
  Revenue: sortByNumber,
  RokuAdKeys: sortByString,
  SamsungModel: sortByString,
  SegmentIDs: sortByString,
  SegmentName: sortByString,
  SessionsPerUser: sortByNumber,
  SetTargetings: sortByNumber,
  SharedFilledVMAPDuration: sortByNumber,
  SharedRequestedVMAPDuration: sortByNumber,
  ShareOfVoice: sortByNumber,
  ShareOfVoiceType: sortByNumber,
  ShareType: sortByNumber,
  SiteID: sortByNumber,
  SiteName: sortByString,
  SitePage: sortByString,
  SlotNumber: sortByNumber,
  Skip: sortByNumber,
  SkipOffset: sortByNumber,
  SOVInventoryType: sortByString,
  SOVParallelAuctionsEnabled: sortByNumber,
  SOVParallelAuctionsBackfillEnabled: sortByNumber,
  SpotxEndpoint: sortByNumber,
  SpotxParamChannelID: sortByNumber,
  StartDate: sortByString,
  StreamAdsFetchTimeout: sortByNumber,
  StreamChunklistTimeout: sortByNumber,
  StreamError: sortByNumber,
  StreamErrorCode: sortByString,
  StreamFormatType: sortByString,
  StreamPlaylistTimeout: sortByNumber,
  StreamStart: sortByNumber,
  TotalRequestedPodDuration: sortByNumber,
  TotalFilledPodDuration: sortByNumber,
  TotalPaidFilledPodDuration: sortByNumber,
  TotalDeliveredPodDuration: sortByNumber,
  SyntheticID: sortByNumber,
  TargetingDevice: sortByString,
  TopCountries: sortByString,
  TrafficType: sortByString,
  UniqueSessions: sortByNumber,
  UniqueUsers: sortByNumber,
  UniqueViewers: sortByNumber,
  UsPrivacy: sortByString,
  VastErrorCode: sortByString,
  VastErrors: sortByNumber,
  ViewsPerSession: sortByNumber,
  VMAPLatency: sortByNumber,
  VMAPRequests: sortByNumber,
  VMAPResponses: sortByNumber,
  VenueID: sortByString,
  VPAIDErrors: sortByNumber,
  VPAIDStart: sortByNumber,
  VPAIDImpressions: sortByNumber,
  VPAIDQuartile1: sortByNumber,
  VPAIDQuartile2: sortByNumber,
  VPAIDQuartile3: sortByNumber,
  VPAIDComplete: sortByNumber,
  WeekOfYear: sortByNumber,
  WinRate: sortByNumber,
  Zip: sortByString,

  // IAS First and Third Party Group Bys
  MediaTypeID: sortByString,
  // IAS First and Third Party Metrics
  TrackedAds: sortByNumber,
  ImpressionRendered: sortByNumber,
  IVTAds: sortByNumber,
  IVTRate: sortByNumber,
  MeasuredAds: sortByNumber,
  MeasuredRate: sortByNumber,
  ViewableImpression: sortByNumber,
  ViewabilityRate: sortByNumber,
  // IAS First Party Metrics (render rate already exist)
  EligibleAds: sortByNumber,
  AdBlockCount: sortByNumber,
  AdBlockRate: sortByNumber,
  // IAS First Party Group Bys (LineItemID, CreativeID, CreativeName, AdvertiserName, OrderName already exist)
  AdvertiserID: sortByString,
  AdUnitID: sortByString,
  AdSize: sortByString,
  OrderID: sortByString,
  AdUnitPath: sortByString,
  LineItemName: sortByString,
  // IAS Third Party Group Bys
  PublicaChannelID: sortByString,
  PublicaBidderID: sortByString,
  PublisherID: sortByString,
  PlacementID: sortByString,
  // IAS Third Party Metrics
  GIVTAds: sortByNumber,
  GIVTRate: sortByNumber,
  SIVTAds: sortByNumber,
  SIVTRate: sortByNumber,
  URLFailRate: sortByNumber,
  IVTFailRate: sortByNumber,
  ValidVideoAds: sortByNumber,
  Quartile1Rate: sortByNumber,
  Quartile2Rate: sortByNumber,
  Quartile3Rate: sortByNumber,
  OnscreenComplete: sortByNumber,
  OnscreenCompletionRate: sortByNumber,
  PassedImps: sortByNumber,
  PassRate: sortByNumber,
  FailedImps: sortByNumber,
  FailedAdRate: sortByNumber,
  ValidViewableVideoAds: sortByNumber,
  ViewableQuartile1: sortByNumber,
  ViewableQuartile2: sortByNumber,
  ViewableQuartile3: sortByNumber,
  ViewableComplete: sortByNumber,
  ViewableQuartile1Rate: sortByNumber,
  ViewableQuartile2Rate: sortByNumber,
  ViewableQuartile3Rate: sortByNumber,
  ViewableCompleteRate: sortByNumber,
  UnmeasuredImps: sortByNumber,
  UnmeasuredImpsRate: sortByNumber,
  NonViewableImpression: sortByNumber,
  NonViewableImpRate: sortByNumber,
  // IAS Performance Report Metrics
  MrcOutOfViewNEPImps: sortByNumber,
  MrcOutOfViewNIVImps: sortByNumber,
  MrcOutOfViewOOFImps: sortByNumber,
  MrcOutOfViewNSImps: sortByNumber,
  MrcOutOfViewUNKImps: sortByNumber,
  BlockedContentImps: sortByNumber,
  BlockedCustomListImps: sortByNumber,
  BlockedGeoImps: sortByNumber,
  BlockedKeywordImps: sortByNumber,
  BlockedSuitabilityImps: sortByNumber,
  BlockedSeeThroughImps: sortByNumber,
  BlockedSuspiciousImps: sortByNumber,
  BlockedLanguageImps: sortByNumber,
  FraudGivtImps: sortByNumber,
  FraudNhtImps: sortByNumber,
  FraudHaImps: sortByNumber,
  FraudDsImps: sortByNumber,
  FraudIbImps: sortByNumber,
  FraudLspImps: sortByNumber,
  FraudSivtImps: sortByNumber,
  RefreshImps: sortByNumber,
  GroupmFullyInViewVideoImps: sortByNumber,
  GroupmFullyInViewDisplayImps: sortByNumber,
  TotalBillableImps: sortByNumber,
  SittingDuckBotImps: sortByNumber,
  StandardBotImps: sortByNumber,
  VolunteerBotImps: sortByNumber,
  ProfileBotImps: sortByNumber,
  MaskedBotImps: sortByNumber,
  NomadicBotImps: sortByNumber,
  OtherBotImps: sortByNumber,
  FailedAdultAds: sortByNumber,
  FailedAlcoholAds: sortByNumber,
  FailedHateSpeechAds: sortByNumber,
  FailedIllegalDownloadsAds: sortByNumber,
  FailedDrugsAds: sortByNumber,
  FailedOffensiveLanguageAds: sortByNumber,
  RefreshThresholdEvents: sortByNumber,
  PublicisFullyInViewDisplayImps: sortByNumber,
  PublicisFullyInViewVideoImps: sortByNumber,
  MrcOvrNetImps: sortByNumber,
  MrcOvrNepImps: sortByNumber,
  MrcOvrNivImps: sortByNumber,
  MrcOvrOofImps: sortByNumber,
  MrcOvrNsImps: sortByNumber,
  MrcOvrUnkImps: sortByNumber,
  FailedContentImps: sortByNumber,
  FailedSuitabilityImps: sortByNumber,
  FailedKeywordImps: sortByNumber,
  FailedCustomListImps: sortByNumber,
  FailedGeoImps: sortByNumber,
  FailedSuspiciousImps: sortByNumber,
  FailedSeeThroughImps: sortByNumber,
  FailedLanguageImps: sortByNumber,
  BlockedContentAdultImps: sortByNumber,
  BlockedContentAlcoholImps: sortByNumber,
  BlockedContentHateImps: sortByNumber,
  BlockedContentDownloadsImps: sortByNumber,
  BlockedContentDrugsImps: sortByNumber,
  BlockedContentOffensiveImps: sortByNumber,
  BlockedContentViolenceImps: sortByNumber,
  SophisticatedInvalidTrafficAds: sortByNumber,
  ReducedValueInventoryAds: sortByNumber,
  FraudGIVTRate: sortByNumber,
  FraudNHTRate: sortByNumber,
  FraudHARate: sortByNumber,
  FraudDSRate: sortByNumber,
  BlockedContentRate: sortByNumber,
  BlockedCustomListRate: sortByNumber,
  BlockedGeoRate: sortByNumber,
  BlockedKeywordRate: sortByNumber,
  BlockedSuitabilityRate: sortByNumber,
  BlockedSeeThroughRate: sortByNumber,
  BlockedSuspiciousRate: sortByNumber,
  BlockedLanguageRate: sortByNumber,
  RefreshedAdRate: sortByNumber,
  IASRefreshedAdRate: sortByNumber,
  PublicisDisplayViewableRate: sortByNumber,
  PublicisVideoViewableRate: sortByNumber,
  InViewAtPageOpenRate: sortByNumber,
  InViewOver5sRate: sortByNumber,
  InViewOver15sRate: sortByNumber,
  VideoInViewRate: sortByNumber,
  VideoOutOfViewRate: sortByNumber,
  VideoNeverStartedRate: sortByNumber,
  VideoCompletedRate: sortByNumber,
  VideoCompletedInViewRate: sortByNumber,
  VideoMutedRate: sortByNumber,
  VideoFullScreenRate: sortByNumber,
  VideoClickThroughRate: sortByNumber,
  GeneralInvalidTrafficRate: sortByNumber,
  SophisticatedInvalidTrafficRate: sortByNumber,
  ReducedValueInventoryRate: sortByNumber,
  ReviewStatus: sortByNumber,
  NumberOfBlocks: sortByString,
  Username: sortByString,
  Timestamp: sortByString,
  PrebidErrors: sortByNumber,
  PostbidErrors: sortByNumber,
  BidRejections: sortByNumber,

  // Elea AI
  EleaAIBidErrors: sortByNumber,
  EleaAIBidErrorsDifferentAdomain: sortByNumber,
  EleaAIBidErrorsDifferentAdomainRate: sortByNumber,
  EleaAIBidLosts: sortByNumber,
  EleaAIBidLostsDifferentAdomain: sortByNumber,
  EleaAIBidLostsDifferentAdomainRate: sortByNumber,
  EleaAIBidResponses: sortByNumber,
  EleaAIBidResponsesDifferentAdomain: sortByNumber,
  EleaAIBidResponsesDifferentAdomainRate: sortByNumber,
  EleaAIBidWons: sortByNumber,
  EleaAIBidWonsDifferentAdomain: sortByNumber,
  EleaAIBidWonsDifferentAdomainRate: sortByNumber,
  EleaAIDetectedLanguage: sortByString,
  EleaAIEnrichedAdomain: sortByString,
  EleaAIImpressions: sortByNumber,
  EleaAIImpressionsDifferentAdomain: sortByNumber,
  EleaAIImpressionsDifferentAdomainRate: sortByNumber,
  EleaAIMediafile: sortByString,
  EleaAIOriginalAdomain: sortByString,
  EleaAIDetectedLanguageBlockedBids: sortByNumber,
  EleaAIBidWonRate: sortByNumber,
  EleaAIImpressionRate: sortByNumber,
  EleaAINumTotalAdomainsBidErrors: sortByNumber,
  EleaAINumTotalAdomainsBidLosts: sortByNumber,
  EleaAINumTotalAdomainsBidResponses: sortByNumber,
  EleaAINumTotalAdomainsBidWons: sortByNumber,
  EleaAINumTotalAdomainsImpressions: sortByNumber,
  EleaAINumTotalDetectedLanguagesBidErrors: sortByNumber,
  EleaAINumTotalDetectedLanguagesBidLosts: sortByNumber,
  EleaAINumTotalDetectedLanguagesBidResponses: sortByNumber,
  EleaAINumTotalDetectedLanguagesBidWons: sortByNumber,
  EleaAINumTotalDetectedLanguagesImpressions: sortByNumber,
  EleaAINumTotalMediafilesBidErrors: sortByNumber,
  EleaAINumTotalMediafilesBidLosts: sortByNumber,
  EleaAINumTotalMediafilesBidResponses: sortByNumber,
  EleaAINumTotalMediafilesBidWons: sortByNumber,
  EleaAINumTotalMediafilesImpressions: sortByNumber,
  EleaAINumUniqueAdomainsBidErrors: sortByNumber,
  EleaAINumUniqueAdomainsBidLosts: sortByNumber,
  EleaAINumUniqueAdomainsBidResponses: sortByNumber,
  EleaAINumUniqueAdomainsBidWons: sortByNumber,
  EleaAINumUniqueAdomainsImpressions: sortByNumber,
  EleaAINumUniqueDetectedLanguagesBidErrors: sortByNumber,
  EleaAINumUniqueDetectedLanguagesBidLosts: sortByNumber,
  EleaAINumUniqueDetectedLanguagesBidResponses: sortByNumber,
  EleaAINumUniqueDetectedLanguagesBidWons: sortByNumber,
  EleaAINumUniqueDetectedLanguagesImpressions: sortByNumber,
  EleaAINumUniqueMediafilesBidErrors: sortByNumber,
  EleaAINumUniqueMediafilesBidLosts: sortByNumber,
  EleaAINumUniqueMediafilesBidResponses: sortByNumber,
  EleaAINumUniqueMediafilesBidWons: sortByNumber,
  EleaAINumUniqueMediafilesImpressions: sortByNumber,
  // Pod Hourly Report
  AdvertiserRuleCpmRev: sortByNumber,
  AdvertiserRuleCpmsRev: sortByNumber,
  AdvertiserRulePodLift: sortByNumber,
  CpmRev: sortByNumber,
  CpmsRev: sortByNumber,
  EventPodCompare: sortByNumber,
  EventPodCompareWithAdvertiserRules: sortByNumber,
  EventPodCompareWithoutAdvertiserRules: sortByNumber,
  IsPhiloFallbackAuction: sortByString,
  NoBidSaverRev: sortByNumber,
  NumberOfWinners: sortByNumber,
  PodComparePodDuration: sortByNumber,
  PodCompareRevenue: sortByNumber,
  PodCompareType: sortByNumber,
  PodDurationBackfilled: sortByNumber,
  PodDurationOwnedByNonSOVBidder: sortByNumber,
  PodDurationOwnedBySOVBidder: sortByNumber,
  PodLevelSOVType: sortByNumber,
  TTDResponses: sortByNumber,
  TTDSpend: sortByNumber,
  TTDWins: sortByNumber,
  TTDLosses: sortByNumber,
  TTDLossesDoNotAir: sortByNumber,
  TTDLossesCompSepSelf: sortByNumber,
  TTDLossesCompSepOther: sortByNumber,
  TTDLossesPriceOrTier: sortByNumber,
  TTDLossesOther: sortByNumber,
  HasAdvertiserRuleResponses: sortByString,
  // Third Party Vast Tracker Domain
  VastTrackerEndpointDomain: sortByString,
  TTDLossesPrice: sortByNumber,
  TTDLossesTier: sortByNumber,
  // Bid Floor Settings Report Only
  BidFloor: sortByNumber,
};
